import { useState, useEffect } from 'react';
import { BookOpen, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Markdown } from './Markdown';

interface DocumentationViewerProps {
  filename: string;
  title: string;
  description?: string;
  showBackButton?: boolean;
}

export function DocumentationViewer({ 
  filename, 
  title, 
  description,
  showBackButton = true 
}: DocumentationViewerProps) {
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Fetch markdown from public folder
    fetch(`/${filename}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load documentation: ${response.statusText}`);
        }
        return response.text();
      })
      .then((text) => {
        setMarkdown(text);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [filename]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          {showBackButton && (
            <Link
              to="/prioritization/documentation"
              className="text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
          )}
          <BookOpen className="h-8 w-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">{title}</h1>
        </div>
        {description && (
          <p className="text-[var(--text-sec)]">{description}</p>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card text-center py-12">
          <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary-600 animate-spin" />
          <p className="text-[var(--text-sec)]">Loading documentation...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-[var(--coral)] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-[var(--coral)] mb-1">Error Loading Documentation</h3>
              <p className="text-sm text-[var(--coral)]">{error}</p>
              <p className="text-xs text-[var(--coral)] mt-2">
                Please ensure {filename} exists in the public folder or contact support.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Markdown Content */}
      {!loading && !error && markdown && (
        <div className="card">
          <div className="max-w-none">
            <Markdown>{markdown}</Markdown>
          </div>
        </div>
      )}
    </div>
  );
}






